import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import axios from 'axios';
import { Agency } from '../types/User';
import { UserRole, Permission, hasPermission, canAccessFeature, PERMISSIONS } from '../types/RoleSystem';
import { UserDetails } from '../types/User';
import { AUTH_ENDPOINTS, SUBSCRIPTION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import supabase from '../utils/supabase';
import { determinePostAuthRoute, PostAuthDestination } from '../utils/paymentRouting';

// This is a comment to try to update vercel build so stop build error

interface PaymentStatusResponse {
  isPaymentValid: boolean;
  paymentStatus: string;
  paidUntil: string | null;
  numberOfSeats: number;
  packagePrice: number | null;
}

async function fetchMe(token: string): Promise<{ success: true; user: UserDetails } | { success: false; error: string }> {
  try {
    const response = await axios.get(AUTH_ENDPOINTS.ME, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const userData = response.data;

    const user: UserDetails = {
      id: userData.id,
      // Core Identity
      fullNames: userData.fullNames ?? userData.firstName ?? null,
      lastName: userData.lastName ?? null,
      fullName: userData.fullName || userData.displayName || userData.email,
      displayName: userData.displayName || userData.fullName || userData.email,
      email: userData.email,
      idNumber: userData.idNumber ?? null,
      identificationType: userData.identificationType ?? null,
      dateOfBirth: userData.dateOfBirth ?? null,
      gender: userData.gender ?? null,
      mobileNumber: userData.mobileNumber ?? null,
      // Physical address fields
      unitNumber: userData.unitNumber ?? null,
      complex: userData.complex ?? null,
      streetNumber: userData.streetNumber ?? null,
      streetName: userData.streetName ?? null,
      suburb: userData.suburb ?? null,
      city: userData.city ?? null,
      postalCode: userData.postalCode ?? null,
      fullPhysicalAddress: userData.fullPhysicalAddress ?? null,
      // Postal address fields (new structured shape)
      postalAddressSameAsResidential: userData.postalAddressSameAsResidential ?? true,
      postalUnitNumber: userData.postalUnitNumber ?? null,
      postalComplex: userData.postalComplex ?? null,
      postalStreetNumber: userData.postalStreetNumber ?? null,
      postalStreetName: userData.postalStreetName ?? null,
      postalSuburb: userData.postalSuburb ?? null,
      postalCity: userData.postalCity ?? null,
      postalPostalCode: userData.postalPostalCode ?? null,
      // Account status
      accountStatus: userData.accountStatus ?? null,
      profileComplete: userData.profileComplete ?? false,
      agency: userData.agency ?? null,
      role: userData.role as UserDetails['role'],
      roleName: userData.roleName ?? null,
      permissions: userData.permissions ?? [],
      accessLevel: userData.accessLevel ?? 4,
      archived: userData.archived ?? false,
      lastLogin: userData.lastLogin ? new Date(userData.lastLogin) : undefined,
      createdAt: new Date(userData.createdAt),
      updatedAt: new Date(userData.updatedAt),
      emailVerified: userData.emailVerified ?? false
    };

    return { success: true, user };
  } catch (err: any) {
    return { success: false, error: err.response?.data?.message || 'Failed to fetch user details' };
  }
}

async function fetchSubscriptionStatus(token: string): Promise<PaymentStatusResponse | null> {
  try {
    const response = await axios.get(SUBSCRIPTION_ENDPOINTS.STATUS, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data as PaymentStatusResponse;
  } catch (err) {
    console.error('Error checking payment status:', err);
    return null;
  }
}

interface EnhancedAuthState {
  isLoggedIn: boolean;
  userDetails: UserDetails | null;
  userReady: boolean;
  loading: boolean;
  error: string | null;
}

interface EnhancedAuthContextType extends EnhancedAuthState {
  // Authentication Methods
  handleLogin: () => Promise<PostAuthDestination>;
  handleLogout: () => void;
  handleUserDetailsUpdate: (updatedUser: Partial<UserDetails>) => void;
  updateAgencyDetails: (updatedAgency: Partial<Agency>) => void;
  checkPaymentStatus: () => Promise<Agency | null>;
  
  // Permission Methods
  hasPermission: (permissionId: string) => boolean;
  canAccessFeature: (requiredPermissions: string[]) => boolean;
  getUserRole: () => UserRole | null;
  getPermissions: () => Permission[];
  
  // Role Checks (Convenience methods)
  isSuperAdmin: () => boolean;
  isAdmin: () => boolean;
  isManager: () => boolean;
  isAgent: () => boolean;
  
  // Feature Access Checks
  canManageCompanyDocuments: () => boolean;
  canViewAllUserDocuments: () => boolean;
  canEditAllUserDocuments: () => boolean;
  canInviteUsers: () => boolean;
  canManageAgency: () => boolean;
  canAccessEducationAdmin: () => boolean;
}

const EnhancedAuthContext = createContext<EnhancedAuthContextType | undefined>(undefined);

interface EnhancedAuthProviderProps {
  children: ReactNode;
}

export const EnhancedAuthProvider: React.FC<EnhancedAuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [userReady, setUserReady] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const hasBootstrappedRef = useRef(false);

  const handleLogout = useCallback(() => {
    setIsLoggedIn(false);
    setUserDetails(null);
    setUserReady(false);
    setError(null);
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_AGENCY);
    supabase.auth.signOut();
  }, []);

  const handleLogin = useCallback(async (): Promise<PostAuthDestination> => {
    setLoading(true);
    setUserReady(false);
    setUserDetails(null);
    setIsLoggedIn(false);
    setError(null);

    try {
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token) {
        throw new Error('No token');
      }

      const result = await fetchMe(token);

      if (!result.success) {
        throw new Error(result.error);
      }

      const userDetails = result.user;

      setUserDetails(userDetails);
      setIsLoggedIn(true);
      setUserReady(true);

      return determinePostAuthRoute(userDetails.agency);
    } catch (err) {
      console.error('Login error:', err);
      setIsLoggedIn(false);
      setUserDetails(null);
      setUserReady(true);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async (): Promise<Agency | null> => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token || !userDetails?.agency) {
      return null;
    }

    const paymentData = await fetchSubscriptionStatus(token);
    if (!paymentData) {
      return null;
    }

    const updatedAgency: Agency = {
      ...userDetails.agency,
      paymentStatus: paymentData.paymentStatus as Agency['paymentStatus'],
      paidUntil: paymentData.paidUntil,
      isPaymentValid: paymentData.isPaymentValid,
      numberOfSeats: paymentData.numberOfSeats,
      packagePrice: paymentData.packagePrice ?? undefined
    };

    setUserDetails(prev => (prev ? { ...prev, agency: updatedAgency } : prev));
    return updatedAgency;
  }, [userDetails]);

  const handleUserDetailsUpdate = useCallback((updatedUser: Partial<UserDetails>) => {
    if (userDetails) {
      setUserDetails({
        ...userDetails,
        ...updatedUser
      });
    }
  }, [userDetails]);

  const updateAgencyDetails = useCallback((updatedAgency: Partial<Agency>) => {
    if (userDetails && userDetails.agency) {
      setUserDetails({
        ...userDetails,
        agency: { ...userDetails.agency, ...updatedAgency }
      });
    }
  }, [userDetails]);

  // Permission checking methods
  const hasPermissionCheck = useCallback((permissionId: string): boolean => {
    if (!userDetails) return false;
    return hasPermission(userDetails.role, permissionId);
  }, [userDetails]);

  const canAccessFeatureCheck = useCallback((requiredPermissions: string[]): boolean => {
    if (!userDetails) return false;
    return canAccessFeature(userDetails.role, requiredPermissions);
  }, [userDetails]);

  const getUserRole = useCallback((): UserRole | null => {
    return userDetails?.role || null;
  }, [userDetails]);

  const getPermissions = useCallback((): Permission[] => {
    return userDetails?.permissions || [];
  }, [userDetails]);

  // Role checking convenience methods
  const isSuperAdmin = useCallback(() => userDetails?.role === 'Super Admin', [userDetails]);
  const isAdmin = useCallback(() => userDetails?.role === 'Admin', [userDetails]);
  const isManager = useCallback(() => userDetails?.role === 'Manager', [userDetails]);
  const isAgent = useCallback(() => userDetails?.role === 'Agent', [userDetails]);

  // Feature access convenience methods
  const canManageCompanyDocuments = useCallback(() => 
    hasPermissionCheck(PERMISSIONS.DOCUMENT_MANAGE_COMPANY.id), [hasPermissionCheck]);
  
  const canViewAllUserDocuments = useCallback(() => 
    hasPermissionCheck(PERMISSIONS.DOCUMENT_VIEW_ALL_USERS.id), [hasPermissionCheck]);
  
  const canEditAllUserDocuments = useCallback(() => 
    hasPermissionCheck(PERMISSIONS.DOCUMENT_EDIT_ALL_USERS.id), [hasPermissionCheck]);
  
  const canInviteUsers = useCallback(() => 
    hasPermissionCheck(PERMISSIONS.USER_INVITE.id), [hasPermissionCheck]);
  
  const canManageAgency = useCallback(() => 
    hasPermissionCheck(PERMISSIONS.AGENCY_MANAGE_OWN.id) || hasPermissionCheck(PERMISSIONS.AGENCY_MANAGE_ALL.id), [hasPermissionCheck]);
  
  const canAccessEducationAdmin = useCallback(() => 
    hasPermissionCheck(PERMISSIONS.EDUCATION_ADMIN.id), [hasPermissionCheck]);

  // Bootstrap: run exactly once on mount, regardless of any ref changes later.
  useEffect(() => {
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      handleLogin();
    } else {
      setUserReady(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo((): EnhancedAuthContextType => ({
    isLoggedIn,
    userDetails,
    userReady,
    loading,
    error,
    handleLogin,
    handleLogout,
    handleUserDetailsUpdate,
    updateAgencyDetails,
    checkPaymentStatus,
    hasPermission: hasPermissionCheck,
    canAccessFeature: canAccessFeatureCheck,
    getUserRole,
    getPermissions,
    isSuperAdmin,
    isAdmin,
    isManager,
    isAgent,
    canManageCompanyDocuments,
    canViewAllUserDocuments,
    canEditAllUserDocuments,
    canInviteUsers,
    canManageAgency,
    canAccessEducationAdmin,
  }), [
    isLoggedIn, userDetails, userReady, loading, error,
    handleLogin, handleLogout, handleUserDetailsUpdate,
    updateAgencyDetails, checkPaymentStatus,
    hasPermissionCheck, canAccessFeatureCheck, getUserRole, getPermissions,
    isSuperAdmin, isAdmin, isManager, isAgent,
    canManageCompanyDocuments, canViewAllUserDocuments, canEditAllUserDocuments,
    canInviteUsers, canManageAgency, canAccessEducationAdmin,
  ]);

  return <EnhancedAuthContext.Provider value={value}>{children}</EnhancedAuthContext.Provider>;
};

export const useEnhancedAuth = (): EnhancedAuthContextType => {
  const context = useContext(EnhancedAuthContext);
  if (!context) {
    throw new Error('useEnhancedAuth must be used within an EnhancedAuthProvider');
  }
  return context;
};
